import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main, download } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { DataService, DebtRecord, DiscountRecord, InvoiceRecord, PartnerRecord } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { TagsService } from '@core/services/tags.service';
import { Multilingual } from '@core/utilities/multilingual';
import { Storage } from '@core/utilities/storage';
import { XLSX } from '@core/utilities/xlsx';
import { ViewerComponent as DiscountViewer } from '@routes/discounts/viewer/viewer.component';
import { CustomerDebtEditorComponent } from '@routes/editors/customer-debt-editor/customer-debt-editor.component';
import { CustomerPaymentEditorComponent } from '@routes/editors/customer-payment-editor/customer-payment-editor.component';
import { ProviderPaymentEditorComponent } from '@routes/editors/provider-payment-editor/provider-payment-editor.component';
import { ViewerComponent as InvoiceViewer } from '@routes/invoices/viewer/viewer.component';
import { ViewerComponent as PaymentViewer } from '@routes/payments/viewer/viewer.component';
import { map } from 'rxjs/operators';
import { ViewerComponent } from './viewer/viewer.component';
import { DatePipe } from '@angular/common';

function convert(records: DebtRecord[], yearStamp: number): DebtRecordEx[] {

  records = records.order(i => i.Stamp, 'ascending');

  let turnover = 0;
  let positive = records.filter(i => i.Total > 0);
  let negative = records.filter(i => i.Total < 0);        

  let nextStamp = new Date(yearStamp).addYears(1).getTime();

  records = records.filter(i => i.Stamp < nextStamp);

  let initial = records.filter(i => i.Stamp < yearStamp);

  if (initial.length > 0) {
    records = [{
      ID: 0,
      Name: 'CK',
      Total: initial.sum(i => i.Total),
      Symbol: initial[0].Symbol,
      Icon: initial[0].Icon,
      Type: 'initial',
      Stamp: yearStamp,
      Reason: 'CK'
    } as DebtRecord].union(records.skip(initial.length));

  }

  return records.map(i => {

    turnover += i.Total;

    let counters = i.Total > 0 ? negative : positive;
    let left = Math.abs(i.Total);

    let result: DebtRecordEx = { ...i, Turnover: turnover, Counters: [], Left: i.Total };

    while (counters.length > 0 && left > 0) {

      let amount = Math.abs(counters[0].Total);

      if (left >= amount) {
        left -= amount;
        result.Counters.push(counters.shift());
      }
      else {
        result.Counters.push({ ...counters[0], Total: i.Total > 0 ? -left : left });
        counters[0] = { ...counters[0], Total: i.Total > 0 ? left - amount : amount - left };
        left = 0;
      }
    }

    result.Left += result.Counters.sum(i => i.Total);    

    return result;
  }).reverse();
}

interface CustomerDebt {
  id: number;
  initial: number;
  raised: number;
  returned: number;
}

interface DebtRecordEx extends DebtRecord {
  Turnover: number;
  Counters: DebtRecord[];
  Left: number;
}

@Component({
  templateUrl: './debts.component.html',
  styleUrls: ['./debts.component.scss']
})
export class DebtsComponent implements OnInit {

  private allDebts: DebtRecord[] = [];

  public get mode(): 'customers' | 'provider' {
    return Storage.get(location.pathname, 'customers');
  }

  public set mode(value: 'customers' | 'provider') {
    Storage.set(location.pathname, value);
  }
  
  public years: number[];
  private _yearStamp: number;

  public get yearStamp(): number {
    return this._yearStamp;
  }

  public set yearStamp(stamp: number) {

    if (stamp == this._yearStamp) return;
    this._yearStamp = stamp;

    let nextYear = new Date(stamp).addYears(1).getTime();

    this.customers.source = this.allDebts.filter(i => i.ID > 2 && i.Stamp < nextYear).group(i => i.ID).map(i => ({
      id: i.key as number,
      initial: i.items.filter(x => x.Type == 'initial' || x.Stamp < stamp).sum(x => -x.Total),
      raised: i.items.filter(x => x.Type == 'raised' && x.Stamp >= stamp).sum(x => -x.Total),
      returned: i.items.filter(x => x.Type == 'payment' && x.Total > 0 && x.Stamp >= stamp).sum(x => x.Total)
    }));  
    
    if (this.displayingPartner) {
      this.display(this.displayingPartner.ID);
    }
  }

  constructor(
    private ds: DataService,
    private modal: ModalService,
    private layout: LayoutService,
    private sync: SynchronizationService,
    private partners: PartnersService,
    private tags: TagsService,
    private date: DatePipe
  ) { }
  public get loading(): boolean {
    return !this.sync.ready(PartnersService, TagsService, ApiService, DebtsComponent);
  }

  public get now(): Date {
    return new Date();
  }

  public print() {
    window.print();
  }

  public customers = new Filterable<CustomerDebt>({
    extractor: i => [this.partner(i).Name, i.id],
    grouper: i => [
      { name: 'Khu vực', option: this.partners.get(i.id).Address.toUpperCase() },
      { name: 'Nợ tồn đọng', option: i.initial != 0 ? 'Có' : 'Không' },
      { name: 'Nợ trong kỳ', option: i.raised != 0 ? 'Có' : 'Không' },
      { name: 'Công nợ', option: i.returned < i.initial ? 'Còn nợ tồn đọng' : i.returned < i.initial + i.raised ? 'Hết nợ tồn đọng' : i.returned > i.initial + i.raised ? 'Trả thừa tiền' : i.returned > 0 ? 'Hết nợ' : 'Không nợ' }
    ]
  });

  public displaying = new Filterable<DebtRecordEx>();
  public displayingPartner: PartnerRecord = null;

  ngOnInit(): void {
    main(() => {
      this.layout.title = Multilingual.getText("Công nợ");
      this.layout.media('header', 'screen');
    });
    this.synchronize();
  }

  public synchronize() {    

    switch (this.mode) {

      case 'customers':

        if (this.displayingPartner !== null && this.displayingPartner.ID == 1) this.displayingPartner = null;        

        let debts: DebtRecord[] = [];
        this.displaying.source = [];
        this.years = undefined;
        this.yearStamp = undefined;                

        this.sync.with(DebtsComponent).repeat(
          () => this.ds.debts('customers').pipe(map(data => data ? commit<boolean>(() => debts = data).return(true) : false))
        );

        if (this.displayingPartner != null) this.display(this.displayingPartner.ID);

        this.sync.when(PartnersService, TagsService, DebtsComponent).commit(() => {

          let years = debts.group(i => new Date(i.Stamp).getFullYear()).order(i => i.key).map(i => new Date(i.key as number, 0, 1).getTime());          
          this.allDebts = debts;                           

          this.yearStamp = years.last();
          this.years = years;
        });

        break;

      case 'provider':

        this.display(1);

        break;
    }
  }

  public display(partnerId: number) {

    let debts: DebtRecord[] = [];
    this.displaying.source = [];
   
    this.sync.with(DebtsComponent).repeat(
      () => this.ds.debts('get', { ID: partnerId }).pipe(map(data => data ? commit<boolean>(() => debts = data).return(true) : false))
    );

    this.sync.when(PartnersService, TagsService, DebtsComponent).commit(() => {

      this.years = debts.group(i => new Date(i.Stamp).getFullYear()).order(i => i.key).map(i => new Date(i.key as number, 0, 1).getTime());

      if (this._yearStamp === undefined) {
        this._yearStamp = this.years.last();
      }

      this.displayingPartner = this.partners.get(partnerId);
      this.displaying.source = convert(debts, this.yearStamp);
    });
  }

  public left(debt: CustomerDebt, type: 'good' | 'bad'): number {
    let left = debt.initial + debt.raised - debt.returned;

    switch (type) {
      case 'good': return Math.max(-left, 0);
      case 'bad': return Math.max(left, 0);
      default: return NaN;
    }
  }

  public sum(type: string) {
    switch (type) {
      case 'goodLeft': return this.customers.matches.sum(i => this.left(i, 'good'));
      case 'badLeft': return this.customers.matches.sum(i => this.left(i, 'bad'));
      case 'raised': return this.displayingPartner !== null ? this.displaying.source.sum(i => this.raised(i)) : this.customers.matches.sum(i => i.raised);
      case 'returned': return this.displayingPartner !== null ? this.displaying.source.sum(i => this.returned(i)) : this.customers.matches.sum(i => i.returned);
      default: return this.customers.matches.sum(i => i[type]);
    }
  }

  public reason(debt: DebtRecord): string {
    switch (debt.Type) {
      case 'initial': return 'chuyển kỳ';
      case 'invoice': return debt.Total > 0 ? (this.mode == 'provider' ? 'nhập hàng' : 'mua hàng') : (this.mode == 'provider' ? 'trừ mất' : 'trả hàng');
      case 'payment': return debt.Total > 0 ? 'thu tiền' : 'trả tiền';
      case 'discount': return 'trừ lỗi';
      case 'adjustment': return debt.Reason;
      default: return 'không rõ'
    }
  }

  public related(debt: DebtRecord): string {
    switch (debt.Type) {
      case 'initial': return 'CK';
      default: return debt.Name;
    }
  }

  public status(debt: DebtRecordEx): string {

    if (debt.Left > 0) {
      switch (debt.Type) {
        case 'payment': return 'còn';
        case 'discount': return 'còn';
        default: return 'nợ';
      }
    }
    else {
      switch (debt.Type) {
        case 'payment': return 'đối trừ hết';
        case 'initial': return 'hết nợ';
        case 'adjustment': return 'đối trừ hết';
        case 'discount': return 'đối trừ hết';
        case 'invoice': return debt.Total < 0 ? 'đối trừ hết' : 'hết nợ';
        default: return 'hết nợ';
      }
    }

  }

  public raised(debt: DebtRecord): number {
    return debt.Type == 'payment' ? Math.max(debt.Total, 0) : debt.Total;
  }

  public returned(debt: DebtRecord): number {
    return debt.Type == 'payment' ? Math.max(-debt.Total, 0) : 0;
  }

  public partner(debt: CustomerDebt): PartnerRecord {
    return this.partners.get(debt.id);
  }

  public extractor(field: string): SortableExtractor<CustomerDebt> {
    return {
      extract: i => {
        switch (field) {
          case 'name': return this.partner(i).Name;
          case 'goodLeft': return this.left(i, 'good');
          case 'badLeft': return this.left(i, 'bad');
          default: return i[field];
        }
      }
    }
  }

  public createExpense() {

    switch (this.mode) {
      case 'customers':

        this.modal.show(CustomerPaymentEditorComponent, { state: { partner: this.displayingPartner, mode: 'expense' } }).content.create.subscribe(payment => this.synchronize());
        break;

      case 'provider':

        this.modal.show(ProviderPaymentEditorComponent).content.create.subscribe(payment => this.synchronize());
        break;
    }
  }

  public createIncome() {
    this.modal.show(CustomerPaymentEditorComponent, { state: { partner: this.displayingPartner, mode: 'income' } }).content.create.subscribe(payment => this.synchronize());
  }

  public createDebt() {
    this.modal.show(CustomerDebtEditorComponent, { state: { partner: this.displayingPartner } }).content.create.subscribe(payment => this.synchronize());
  }

  public open(debt: DebtRecordEx) {

    switch (debt.Type) {

      case 'invoice':

        this.ds.invoices('get', { ID: debt.ID }).subscribe(invoices => {

          let modal = this.modal.show(InvoiceViewer, {
            state: {
              invoice: invoices[0],
              payments: debt.Counters.map(i => ({ Amount: Math.abs(i.Total), Tag: this.related(i), Stamp: i.Stamp }))
            }
          });

          modal.content.unconfirmed.subscribe(invoice => {
            this.display(this.displayingPartner.ID);
          });
        });
        break;

      case 'discount':

        this.ds.discounts('get', { ID: debt.ID }).subscribe(discounts => this.modal.show(DiscountViewer, { state: { discount: discounts[0], mode: this.mode == 'provider' ? 'import' : 'export', counters: debt.Counters.map(i => ({ Stamp: i.Stamp, Tag: this.related(i), Amount: Math.abs(i.Total) })) } }));
        break;

      case 'payment':

        this.ds.payments('get', { ID: debt.ID }).subscribe(payments => this.modal.show(PaymentViewer, { state: { payment: payments[0], reasons: debt.Counters.map(i => ({ Stamp: i.Stamp, Tag: this.related(i), Amount: Math.abs(i.Total) })) } }).content.deleted.subscribe(result => this.synchronize()));
        break;

      default:

        let ref = this.modal.show(ViewerComponent, { state: { partner: this.displayingPartner, debt: { ...debt, Name: this.related(debt) }, counters: debt.Counters.map(i => ({ Stamp: i.Stamp, Tag: this.related(i), Amount: Math.abs(i.Total) })) } });
        ref.content.deleted.subscribe(record => this.synchronize());
        break;
    }       
  }

  public exportExcel(mode: 'current' | 'all' = 'current') {

    switch(mode) {
      case 'current': 
        if (this.displayingPartner.ID != 1) {
          this.exportExcelOfCurrentCustomer();           
        }
        else {
          this.exportExcelOfProvider();
        }
        break;
      case 'all': this.exportExcelOfAllCustomers(); break;        
    }
  }

  private exportExcelOfCurrentCustomer() {

    let invoices = new Lookup<InvoiceRecord>(i => i.ID);
    let discounts = new Lookup<DiscountRecord>(i => i.ID);

    this.displaying.source.forEach(debt => {
      switch (debt.Type) {
        case 'invoice': this.sync.with(DebtsComponent).repeat(() => this.ds.invoices('get', { ID: debt.ID }).pipe(map(results => results && results.length == 1 ? commit<boolean>(() => invoices.add(results[0])).return(true) : false))); break;
        case 'discount': this.sync.with(DebtsComponent).repeat(() => this.ds.discounts('get', { ID: debt.ID }).pipe(map(results => results && results.length == 1 ? commit<boolean>(() => discounts.add(results[0])).return(true) : false))); break;
      }
    });

    this.sync.when(DebtsComponent).commit(() => {

      let xlsx = new XLSX();
      let sheet = xlsx.worksheet(this.displayingPartner.Name);

      xlsx.decimalFormat = '##,0.0';

      let rows: any[][] = [];

      rows.push(['Ngày', 'Lý do', 'Chứng từ', 'Mã hàng', 'Mã màu', 'Số lượng', 'Cây 1', 'Cây 2', 'Cây 3', 'Cây 4', 'Cây 5', 'Cây 6', 'Tổng mét', 'Khấu trừ', 'Tính tiền', 'Đơn giá', 'Thành tiền', 'Công nợ'].map(i => i.toUpperCase()));

      ['left', 'left', 'center', 'center', 'center', 'center', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right'].forEach((align, i) => sheet.column(i + 1).align = align as any);

      this.displaying.source.reverse().forEach(debt => {


        switch (debt.Type) {

          case 'invoice':            

            rows.push([new Date(debt.Stamp),this.reason(debt).toUpperCase(), this.related(debt)]);

            let lastSKU = '';

            invoices.get(debt.ID).Details.group(i => [i.SKU, i.Color, i.QuoteUnitPrice].join('/')).forEach(group => {

              let details = group.items;

              rows.last().push(details[0].SKU == lastSKU ? null : details[0].SKU, details[0].Color, details.length);
              lastSKU = details[0].SKU;

              for (let i = 0; i < details.length; i++) {
                if (i > 0 && i % 6 == 0) rows.push([null, null, null, null, null, null]);
                rows.last().push(details[i].ActualQuantity);
              }

              while (rows.last().length < 12) rows.last().push(null);

              rows.last().push(details.sum(i => i.ActualQuantity), details.sum(i => i.ActualQuantity - i.QuoteQuantity), details.sum(i => i.QuoteQuantity), details[0].QuoteUnitPrice, details.sum(i => i.QuoteQuantity * i.QuoteUnitPrice));
              rows.push([null, null, null]);
            });

            rows.pop();
            rows.last().push(debt.Total);

            break;

          case 'discount':

            rows.push([new Date(debt.Stamp), this.reason(debt).toUpperCase()]);

            let lastGroup = '';

            discounts.get(debt.ID).Details.group(i => [i.ExportInvoiceTag, i.SKU, i.Color, i.ExportUnitPrice].join('/')).forEach(group => {

              let details = group.items;

              let test = lastGroup == [details[0].ExportInvoiceTag, details[0].SKU].join('/');

              rows.last().push(test ? null : details[0].ExportInvoiceTag, test ? null : details[0].SKU, details[0].Color, details.length);
              lastGroup = [details[0].ExportInvoiceTag, details[0].SKU].join('/');

              for (let i = 0; i < details.length; i++) {
                if (i > 0 && i % 6 == 0) rows.push([null, null, null, null, null, null]);
                rows.last().push(details[i].ActualQuantity + '-' + details[i].ExportDiscountQuantity);
              }

              while (rows.last().length < 12) rows.last().push(null);

              rows.last().push(null, details.sum(i => i.ExportDiscountQuantity), -details.sum(i => i.ExportDiscountQuantity), details[0].ExportUnitPrice, -details.sum(i => i.ExportDiscountQuantity * i.ExportUnitPrice));
              rows.push([null, null]);
            });

            rows.pop();
            rows.last().push(debt.Total);

            break;

          default: rows.push([new Date(debt.Stamp), this.reason(debt).toUpperCase(), this.related(debt), '', '', '', '', '', '', '', '', '', '', '', '', '', '', debt.Total]); break;
          
        }
      });

      rows.push(['TỔNG', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', this.displaying.source.sum(i => i.Total)]);

      rows.forEach((row, i) => sheet.row(i + 1, ...row));


      for (let x = 1; x < 7; x++) {
        sheet.column(x).valign = 'top';

        for (let y = 2; y < sheet.count('rows'); y++)
          if (sheet.has(x, y)) sheet.cell(x, y).merge('down');
      }

      for (let x = 13; x < 19; x++) {
        sheet.column(x).valign = 'bottom';

        for (let y = sheet.count('rows') - 1; y > 1; y--)
          if (sheet.has(x, y)) sheet.cell(x, y).merge('up');
      }

      sheet.frozenTopRowCount = 1;

      xlsx.generate().subscribe(base64 => download(this.displayingPartner.Name + " (" + this.date.transform(this.now, 'dd-MM-yyyy HH:mm:ss z') + ").xlsx", base64));
    });
  }

  private exportExcelOfAllCustomers() {

    this.sync.when(DebtsComponent).commit(() => {

      let xlsx = new XLSX();
      let sheet = xlsx.worksheet("TỔNG HỢP");

      xlsx.decimalFormat = '##,0.0';

      let rows: any[][] = [];

      rows.push(['Tên khách hàng', 'Mã khách hàng', 'Khu vực', 'Tồn đọng', 'Phát sinh', 'Đã thu', 'Cần thu', 'Cần trả'].map(i => i.toUpperCase()));

      ['left', 'center', 'center', 'right', 'right', 'right', 'right', 'right'].forEach((align, i) => sheet.column(i + 1).align = align as any);
      
      this.customers.source.forEach(customer => {
        rows.push([this.partner(customer).Name, 'K' + customer.id, this.partners.get(customer.id).Address, customer.initial, customer.raised, customer.returned, Math.max(0, customer.initial + customer.raised - customer.returned), Math.max(0, customer.returned - customer.initial - customer.raised)])
      })

      rows.push(['TỔNG', '-', '-',
        this.customers.source.sum(i => i.initial), 
        this.customers.source.sum(i => i.raised), 
        this.customers.source.sum(i => i.returned), 
        this.customers.source.sum(i => Math.max(0, i.initial + i.raised - i.returned)),
        this.customers.source.sum(i => Math.max(0, i.returned - i.initial - i.raised)),
      ]);      
        
      sheet.frozenTopRowCount = 1;

      rows.forEach((row, i) => sheet.row(i + 1, ...row));

      xlsx.generate().subscribe(base64 => download("Tổng hợp công nợ khách hàng (" + this.date.transform(this.now, 'dd-MM-yyyy HH:mm:ss z') + ").xlsx", base64));
    });
  }

  private exportExcelOfProvider() {

    let xlsx = new XLSX();
      let sheet = xlsx.worksheet("CÔNG NỢ VỚI XƯỞNG");

      xlsx.decimalFormat = '##,0.0';

      let rows: any[][] = [];

      rows.push(['Ngày', 'Lý do', 'Chứng từ', 'Phát sinh nợ', 'Phát sinh trả', 'Công nợ còn lại'].map(i => i.toUpperCase()));

      ['center', 'center', 'center', 'right', 'right', 'right'].forEach((align, i) => sheet.column(i + 1).align = align as any);
      
      this.displaying.source.forEach(record => {
        rows.push([new Date(record.Stamp), this.reason(record), this.related(record), this.raised(record), this.returned(record), record.Turnover])
      })      
        
      sheet.frozenTopRowCount = 1;

      rows.forEach((row, i) => sheet.row(i + 1, ...row));

      xlsx.generate().subscribe(base64 => download("Công nợ với xưởng (" + this.date.transform(this.now, 'dd-MM-yyyy HH:mm:ss z') + ").xlsx", base64));
  }
}
